import {
  SkinCheckRecencyEnum,
  YesNoUnknownEnum,
} from '@dermloop/static-values';
import {
  IsBoolean,
  IsEnum,
  IsNotEmpty,
  IsOptional,
  IsString,
  IsUUID,
} from 'class-validator';
import { BaseDto } from '../base.dto';
import { ApproximateAgeDto } from '../clinical-details';
import { UserDto } from '../user.dto';

class Details {
  /**
   * Details in common
   */

  @IsOptional()
  @IsEnum(YesNoUnknownEnum)
  hasTreatments?: YesNoUnknownEnum;

  @IsOptional()
  @IsEnum(YesNoUnknownEnum)
  hasSymptoms?: YesNoUnknownEnum;

  @IsOptional()
  @IsString({ each: true })
  symptoms?: string[];

  @IsOptional()
  @IsString()
  otherSymptoms?: string;

  @IsOptional()
  @IsString()
  otherTreatments?: string;

  @IsOptional()
  @IsUUID()
  ageId?: string;

  @IsOptional()
  @IsString()
  note?: string;

  @IsOptional()
  @IsEnum(YesNoUnknownEnum)
  plannedSkinCheck?: YesNoUnknownEnum;

  @IsOptional()
  @IsEnum(SkinCheckRecencyEnum)
  lastSkinCheckRecency?: SkinCheckRecencyEnum;

  /**
   * Condition details
   */

  @IsOptional()
  @IsString({ each: true })
  treatments?: string[];

  @IsOptional()
  @IsEnum(YesNoUnknownEnum)
  hasAppearanceChanges?: YesNoUnknownEnum;

  @IsOptional()
  @IsString({ each: true })
  appearanceChanges?: string[];

  @IsOptional()
  @IsString()
  otherAppearanceChanges?: string;

  @IsOptional()
  @IsString()
  otherAppearanceChangeAge?: string;

  @IsOptional()
  @IsString()
  otherAge?: string;

  @IsOptional()
  @IsUUID()
  appearanceChangeAgeId?: string;

  @IsOptional()
  @IsString({ each: true })
  macroscopicInspection?: string[];

  @IsOptional()
  @IsString()
  otherMacroscopicInspection?: string;

  @IsOptional()
  @IsUUID()
  symptomAgeId?: string;

  /**
   * Rash details
   */

  @IsOptional()
  @IsEnum(YesNoUnknownEnum)
  hasMedicationOrExposure?: YesNoUnknownEnum;

  @IsOptional()
  @IsString()
  otherMedicationOrExposure?: string;

  @IsOptional()
  @IsEnum(YesNoUnknownEnum)
  hasAlleviatingFactors?: YesNoUnknownEnum;

  @IsOptional()
  @IsString({ each: true })
  alleviatingFactors?: string[];

  @IsOptional()
  @IsString()
  otherAlleviatingFactors?: string;

  @IsOptional()
  @IsEnum(YesNoUnknownEnum)
  hasAggravatingFactors?: YesNoUnknownEnum;

  @IsOptional()
  @IsString({ each: true })
  aggravatingFactors?: string[];

  @IsOptional()
  @IsString()
  otherAggravatingFactors?: string;

  @IsOptional()
  @IsEnum(YesNoUnknownEnum)
  hasGeneralSymptoms?: YesNoUnknownEnum;

  @IsOptional()
  @IsString({ each: true })
  generalSymptoms?: string[];

  @IsOptional()
  @IsString()
  otherGeneralSymptoms?: string;
}

export class CreateConditionDetailsDto extends Details {
  @IsOptional()
  @IsUUID()
  conditionId?: string;

  @IsOptional()
  @IsUUID()
  /** @deprecated Use conditionId */
  lesionId?: string;
}

export class UpdateConditionDetailsDto extends Details {}

export class ConditionDetailsDto extends Details implements BaseDto {
  id: string;
  createdByUser?: UserDto;
  updatedByUser?: UserDto;
  deletedByUser?: UserDto;
  created?: string | Date;
  updated?: string | Date;
  deleted?: string | Date;
  age?: ApproximateAgeDto;

  @IsOptional()
  @IsUUID()
  conditionId?: string;

  @IsOptional()
  @IsUUID()
  /** @deprecated Use conditionId */
  lesionId?: string;

  @IsNotEmpty()
  @IsUUID()
  medicalUnitId?: string;

  medicalUnitName?: string;
}

export class LegacyConditionDetailsDto implements BaseDto {
  id: string;
  createdByUser?: UserDto;
  updatedByUser?: UserDto;
  deletedByUser?: UserDto;
  created?: string | Date;
  updated?: string | Date;
  deleted?: string | Date;

  @IsOptional()
  @IsString()
  conditionId?: string;

  @IsOptional()
  @IsString()
  /** @deprecated Use conditionId */
  lesionId?: string;

  /**
   * Details in common
   */

  @IsNotEmpty()
  @IsBoolean()
  hasTreatments: boolean;

  @IsNotEmpty()
  @IsBoolean()
  hasSymptoms: boolean;

  @IsNotEmpty()
  @IsString({ each: true })
  symptoms: string[];

  @IsNotEmpty()
  @IsString()
  otherSymptoms: string;

  @IsNotEmpty()
  @IsString()
  otherTreatments: string;

  @IsString()
  @IsNotEmpty()
  ageId: string;

  /**
   * Condition details
   */

  @IsNotEmpty()
  @IsString({ each: true })
  treatments: string[];

  @IsNotEmpty()
  @IsBoolean()
  hasAppearanceChanges: boolean;

  @IsNotEmpty()
  @IsString({ each: true })
  appearanceChanges: string[];

  @IsNotEmpty()
  @IsString()
  otherAppearanceChanges: string;

  @IsNotEmpty()
  @IsString()
  otherAppearanceChangeAge: string;

  @IsNotEmpty()
  @IsString()
  otherAge: string;

  @IsNotEmpty()
  @IsString()
  appearanceChangeAgeId: string;

  @IsNotEmpty()
  @IsOptional()
  @IsString({ each: true })
  macroscopicInspection: string[];

  @IsNotEmpty()
  @IsString()
  otherMacroscopicInspection: string;

  @IsNotEmpty()
  @IsString()
  symptomAgeId: string;

  @IsString()
  @IsNotEmpty()
  medicalUnitId: string;
}
